import React from "react";
import classnames from "classnames";
import { HashLink } from "react-router-hash-link";
import { Link } from "react-router-dom";

import { Divider, Typography, Grid, withMobileDialog } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { constants } from "../global";
import LogoFull from "./LogoFull";
import CustomButton from "./CustomButton";
import SquircleBox from "./SquircleBox";
import { CONTENT_FRAME_STYLE, CONTENT_INNER_STYLE } from "../layout";

const styles = (theme) => ({
  root: {
    padding: "2.75rem 0 1.5rem",
    backgroundColor: "#11120E",
    color: theme.palette.white.main,
  },
  frame: {
    ...CONTENT_FRAME_STYLE,
    ...CONTENT_INNER_STYLE,
    maxWidth: "1460px",
    boxSizing: "border-box",
    backgroundColor: "#11120E",
  },
  legalBand: {
    width: "100%",
    backgroundColor: "#11120E",
    boxSizing: "border-box",
    padding: "0 0 1.25rem",
  },
  legalBandInner: {
    ...CONTENT_FRAME_STYLE,
    ...CONTENT_INNER_STYLE,
    maxWidth: "1460px",
    boxSizing: "border-box",
    backgroundColor: "#11120E",
  },
  topCard: {
    borderRadius: "24px",
    padding: "1.2rem",
  },
  socialLinks: {
    color: theme.palette.white.main,
    fontSize: "1.4rem",
  },
  socialLinkButton: {
    width: "44px",
    height: "44px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.white.main,
    borderRadius: "999px",
    marginRight: "0.35rem",
    "&:hover": {
      cursor: "pointer",
      color: theme.palette.secondary.main,
      backgroundColor: "rgba(255,255,255,0.1)",
    },
  },
  links: {
    fontWeight: 700,
    color: theme.palette.white.main,
    "&:hover": { color: theme.palette.secondary.main },
  },
  footerLinks: {
    color: "rgba(255,255,255,0.86)",
    borderRadius: "12px",
    fontWeight: 700,
    whiteSpace: "nowrap",
    "&:hover": {
      color: theme.palette.white.main,
      backgroundColor: "rgba(255,255,255,0.08)",
    },
  },
  greenLink: {
    fontWeight: 800,
    color: theme.palette.primary.dark,
    "&:hover": {
      color: theme.palette.primary.dark,
    },
    cursor: "pointer",
    borderColor: "rgba(149,227,98,0.6)",
    backgroundColor: theme.palette.secondary.main,
    padding: "0.7rem 1.1rem",
    display: "inline-flex",
    alignItems: "center",
  },
  legalStrip: {
    backgroundColor: "#11120E",
    width: "100%",
  },
});

const Footer = (props) => {
  const {
    classes,
    fullScreen,
    setSavingsCalculatorOpen,
    setContactFormOpen,
  } = props;

  const getLinks = () => [
    {
      label: "Como funciona",
      slug: "como-funciona",
    },
    {
      label: "Quem somos",
      slug: "quem-somos",
    },
    {
      label: "Projetos",
      slug: "projetos",
    },
    {
      label: "Glossário",
      route: "/glossario",
    },
    {
      label: "Contato",
      slug: "contato",
      onClick: () => setContactFormOpen(true),
    },
    {
      label: "Simular Economia",
      slug: "simulador-de-economia",
      onClick: () => setSavingsCalculatorOpen(true),
    },
  ];

  const socialIcons = (
    <Typography component="div" variant="h5" style={{ lineHeight: 1 }}>
      <a
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Instagram Profile"
        href={constants.instagramUrl}
        className={classes.socialLinkButton}
      >
        <i className={classnames(["fab fa-instagram", classes.socialLinks])} />
      </a>
      <a
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Linked In Profile"
        href={constants.linkedInUrl}
        className={classes.socialLinkButton}
      >
        <i className={classnames(["fab fa-linkedin", classes.socialLinks])} />
      </a>
      <a
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp"
        href={constants.whatsAppUrl}
        className={classes.socialLinkButton}
      >
        <i className={classnames(["fab fa-whatsapp", classes.socialLinks])} />
      </a>
    </Typography>
  );

  return (
    <Grid container className={classes.root}>
      <Grid item xs={12} className={classes.frame}>
        <SquircleBox
          cornerRadius={24}
          cornerSmoothing={0.8}
          backgroundColor="transparent"
          borderWidth={0}
        >
          <Grid
            container
            className={classes.topCard}
            spacing={fullScreen ? 2 : 3}
          >
            <Grid item xs={12} md={3}>
              <Grid
                container
                alignItems={fullScreen ? "center" : "flex-start"}
                direction="column"
              >
                <Grid item>
                  <LogoFull width={constants.footerLogoWidth} darkMode />
                </Grid>
                <Grid item style={{ marginTop: "1rem" }}>
                  {socialIcons}
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              md={7}
              style={
                fullScreen
                  ? {
                      display: "flex",
                      justifyContent: "center",
                      paddingLeft: "0.75rem",
                      paddingRight: "0.75rem",
                    }
                  : undefined
              }
            >
              <Grid
                container
                direction={fullScreen ? "column" : "row"}
                alignItems={fullScreen ? "center" : "flex-start"}
                justifyContent={fullScreen ? "center" : "flex-start"}
                style={
                  fullScreen
                    ? {
                        width: "100%",
                        maxWidth: "340px",
                      }
                    : undefined
                }
              >
                {getLinks().map((link) => {
                  let component;
                  if (link.onClick) {
                    component = undefined;
                  } else if (link.route) {
                    component = Link;
                  } else {
                    component = HashLink;
                  }
                  const onClick = link.onClick || undefined;
                  const to = link.route || (!link.onClick ? `/#${link.slug}` : undefined);

                  return (
                    <CustomButton
                      key={`footer-link-${link.slug}`}
                      component={component}
                      onClick={onClick}
                      className={classes.footerLinks}
                      variant="text"
                      to={to}
                      size="small"
                      style={
                        fullScreen
                          ? {
                              width: "100%",
                              justifyContent: "center",
                              marginBottom: "0.2rem",
                              paddingLeft: "1.1rem",
                              paddingRight: "1.1rem",
                            }
                          : {
                              marginBottom: "0.15rem",
                              paddingLeft: "0.5rem",
                              paddingRight: "0.5rem",
                            }
                      }
                    >
                      {link.label}
                    </CustomButton>
                  );
                })}
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              md={2}
              style={{
                textAlign: fullScreen ? "center" : "right",
                display: "flex",
                alignItems: fullScreen ? "center" : "center",
                justifyContent: fullScreen ? "center" : "flex-end",
                paddingLeft: fullScreen ? "0.75rem" : 0,
                paddingRight: fullScreen ? "0.75rem" : 0,
              }}
            >
              <CustomButton
                variant="outlined"
                color="green"
                component={Link}
                to="/parceiros"
                className={classnames([classes.greenLink])}
                style={
                  fullScreen
                    ? {
                        width: "100%",
                        maxWidth: "340px",
                        justifyContent: "center",
                      }
                    : undefined
                }
              >
                <i
                  className={classnames(["far", "fa-handshake-simple"])}
                  style={{ marginRight: ".5rem" }}
                />
                Seja nosso parceiro
              </CustomButton>
            </Grid>
          </Grid>
        </SquircleBox>
      </Grid>

      <Grid item xs={12} className={classes.legalBand}>
        <Grid className={classes.legalBandInner}>
          <Grid
            item
            xs={12}
            className={classes.legalStrip}
            style={{ width: "100%", paddingTop: "0.95rem", paddingBottom: ".4rem" }}
          >
            <Divider style={{ background: "rgba(255,255,255,0.18)" }} />
          </Grid>

          <Grid
            container
            className={classes.legalStrip}
            style={{ margin: 0, paddingBottom: "0.25rem" }}
            justifyContent="space-between"
            alignItems={fullScreen ? "flex-start" : "center"}
            spacing={0}
          >
            <Grid item>
              <Grid container alignItems="center">
                <Grid item>
                  <Typography variant="caption" style={{ marginBottom: "1rem" }}>
                    <a
                      className={classes.links}
                      target="_blank"
                      rel="noopener noreferrer"
                      href={constants.privacyPolicyUrl}
                    >
                      Política de Privacidade
                    </a>
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="caption" style={{ margin: "0 .5rem" }}>
                    |
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="caption" style={{ marginBottom: ".5rem" }}>
                    <a
                      className={classes.links}
                      target="_blank"
                      rel="noopener noreferrer"
                      href={constants.termsOfServiceUrl}
                    >
                      Termos de Serviço
                    </a>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Typography
                style={{ color: "rgba(255,255,255,0.75)", marginBottom: "0.15rem" }}
                align={fullScreen ? "left" : "right"}
                variant="caption"
              >
                {`©${new Date().getFullYear()} ${
                  constants.globalCompanyName
                }. Todos os direitos reservados.`}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(withMobileDialog()(Footer));
